/**
 * «Посмотреть рекламу и получить бонус» — для тех, кто упёрся в лимит:
 * +1 урок, +5 к разговору с Тико (сообщения и минуты звонка) или +1 игра
 * (`AD_BONUSES`). Пока это заглушка рекламы: полноэкранный «ролик» на
 * `AD_SECONDS` секунд с обратным отсчётом, после него сервер поднимает
 * лимит (`/api/billing/ad-bonus`, там же событие `ad_watched_bonus_*` в
 * PostHog). Закрыть ролик раньше можно — но без бонуса. Настоящая
 * рекламная сеть встанет на место `FakeAd`.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { AdBonusType, Metric } from '@aptile/shared';

import { claimAdBonus } from '../../api/billing';
import { ApiClientError } from '../../api/client';
import { usePaywallStore } from '../../store/paywallStore';
import { toast } from '../../store/toastStore';
import { Button } from '../Button';
import { IconPlayerPlay, IconX } from '../icons';
import { Quokka } from '../Quokka';

/** Длина ролика-заглушки, с. */
export const AD_SECONDS = 5;

/** Ролик-заглушка: отсчёт и полоса; досмотрен — `onFinished`, закрыт раньше — `onCancel`. */
function FakeAd({ onFinished, onCancel }: { readonly onFinished: () => void; readonly onCancel: () => void }) {
  const { t } = useTranslation();
  const [left, setLeft] = useState(AD_SECONDS);
  // Отсчёт не перезапускается, когда родитель перерисовывается с новой функцией.
  const finished = useRef(onFinished);
  finished.current = onFinished;

  useEffect(() => {
    const started = Date.now();
    const timer = window.setInterval(() => {
      const next = Math.max(AD_SECONDS - Math.floor((Date.now() - started) / 1000), 0);
      setLeft(next);
      if (next === 0) {
        window.clearInterval(timer);
        finished.current();
      }
    }, 200);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label={t('adBonus.adLabel')}
      data-testid="fake-ad"
      className="fixed inset-0 z-[120] grid place-items-center bg-[#1E1813]/90 p-4 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="relative flex w-full max-w-sm flex-col items-center gap-4 rounded-card bg-surface-solid p-6 text-center shadow-card-lg">
        <span className="absolute left-4 top-4 rounded-full bg-heading/10 px-2 py-0.5 font-heading text-xs font-bold uppercase tracking-wide text-muted">{t('adBonus.adLabel')}</span>
        <button
          type="button"
          onClick={onCancel}
          aria-label={t('adBonus.cancel')}
          className="tap-target absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full text-muted transition hover:bg-heading/10 hover:text-heading"
        >
          <IconX size={18} aria-hidden="true" />
        </button>
        <Quokka mood="celebrate" alt="" size="md" />
        <p className="font-heading text-lg font-extrabold text-heading">{t('adBonus.placeholder')}</p>
        <div className="h-2 w-full overflow-hidden rounded-full bg-heading/[0.08]" role="progressbar" aria-valuemin={0} aria-valuemax={AD_SECONDS} aria-valuenow={AD_SECONDS - left}>
          <motion.span className="block h-full rounded-full bg-gradient-to-r from-accent to-teal" initial={{ width: '0%' }} animate={{ width: '100%' }} transition={{ duration: AD_SECONDS, ease: 'linear' }} />
        </div>
        <p aria-live="polite" className="text-sm text-muted">
          {left > 0 ? t('adBonus.countdown', { seconds: left }) : t('adBonus.claiming')}
        </p>
      </div>
    </motion.div>
  );
}

/**
 * Как назвать бонус ученику: разговор с Тико один, но чат меряется
 * сообщениями, а звонок — минутами, поэтому подпись — по исчерпанному лимиту.
 */
type Offer = 'lesson' | 'chat' | 'call' | 'game';

function offerOf(bonusType: AdBonusType, metric: Metric | undefined): Offer {
  switch (bonusType) {
    case 'LESSON':
      return 'lesson';
    case 'GAME_PLAY':
      return 'game';
    case 'CHAT_MINUTES':
      return metric === 'CALL_MINUTES' ? 'call' : 'chat';
  }
}

export function AdBonusButton({ bonusType, metric, className }: {
  readonly bonusType: AdBonusType;
  /** Какой лимит исчерпан — для подписи (сообщения или минуты). */
  readonly metric?: Metric;
  readonly className?: string;
}) {
  const { t } = useTranslation();
  const offer = offerOf(bonusType, metric);
  const bonusGranted = usePaywallStore((state) => state.bonusGranted);
  const [watching, setWatching] = useState(false);

  const finish = () => {
    claimAdBonus(bonusType)
      .then(() => {
        toast.success(t(`adBonus.granted.${offer}`));
        bonusGranted();
      })
      .catch((error: unknown) => {
        toast.error(error instanceof ApiClientError && error.code === 'AD_BONUS_LIMIT' ? t('adBonus.limit') : t('adBonus.failed'));
      })
      .finally(() => setWatching(false));
  };

  return (
    <>
      <Button variant="secondary" fullWidth icon={IconPlayerPlay} onClick={() => setWatching(true)} disabled={watching} data-testid="ad-bonus" className={className}>
        {t(`adBonus.watch.${offer}`)}
      </Button>
      {createPortal(<AnimatePresence>{watching && <FakeAd key="ad" onFinished={finish} onCancel={() => setWatching(false)} />}</AnimatePresence>, document.body)}
    </>
  );
}
